import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Database, Check, X, RefreshCw, Key, Globe, Users, Webhook, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OkGoLogo = () => (
  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center text-white font-black text-sm shadow-lg shadow-red-500/30">
    OG
  </div>
);

export default function OkGoIntegrationPanel() {
  const [cfg, setCfg] = useState({
    api_key: "",
    base_url: "https://app.okgo.mx/api/1.1",
    default_pin: "0000",
    auto_register_enabled: true,
    webhook_secret: "",
  });
  const [configured, setConfigured] = useState(false);
  const [webhookConfigured, setWebhookConfigured] = useState(false);
  const [webhookSecret, setWebhookSecret] = useState("");
  const [keyPreview, setKeyPreview] = useState("");
  const [status, setStatus] = useState(null);
  const [stats, setStats] = useState({ total_okgo_users: 0, still_using_generic_pin: 0 });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const webhookUrl = `${window.location.origin}/api/admin/okgo/webhook/user-created`;

  useEffect(() => {
    loadConfig();
    loadStats();
  }, []);

  const loadConfig = async () => {
    try {
      const r = await axios.get(`${API}/admin/okgo/config`);
      setConfigured(r.data.configured);
      setKeyPreview(r.data.api_key_preview || "");
      setWebhookConfigured(Boolean(r.data.webhook_configured));
      setCfg(c => ({
        ...c,
        base_url: r.data.base_url || c.base_url,
        default_pin: r.data.default_pin || c.default_pin,
        auto_register_enabled: r.data.auto_register_enabled,
      }));
    } catch (e) {/* ignore */}
  };

  const loadStats = async () => {
    try {
      const r = await axios.get(`${API}/admin/okgo/stats`);
      setStats(r.data);
    } catch (e) {/* ignore */}
  };

  const save = async () => {
    if (!cfg.api_key.trim() && !configured) { toast.error("Ingresa la API Key de OkGo"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/okgo/configure`, cfg);
      toast.success("OkGo configurado");
      setConfigured(true);
      setWebhookConfigured(Boolean(r.data.webhook_configured));
      setCfg(current => ({ ...current, webhook_secret: "" }));
      await loadConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando configuración");
    }
    setSaving(false);
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await axios.get(`${API}/admin/okgo/status`);
      setStatus(r.data);
      if (r.data.status === "connected") toast.success("Conexión OK con OkGo");
      else toast.error(r.data.message);
    } catch (e) {
      toast.error("Error verificando estado");
    }
    setTesting(false);
  };

  const sync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await axios.post(`${API}/admin/okgo/sync-users`);
      setSyncResult(r.data);
      toast.success(`Sincronización: ${r.data.created} creados, ${r.data.updated} actualizados`);
      loadStats();
    } catch (e) {
      const msg = e.response?.data?.detail || "Error sincronizando";
      toast.error(typeof msg === "string" ? msg : "Error");
      setSyncResult({ success: false, message: typeof msg === "string" ? msg : "Error" });
    }
    setSyncing(false);
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
    toast.success("Webhook URL copiada");
  };

  const generateWebhookSecret = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/okgo/webhook-secret/generate`);
      setWebhookSecret(r.data.webhook_secret);
      setWebhookConfigured(true);
      toast.success("Secreto generado. Cópialo ahora en Bubble.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo generar el secreto");
    }
    setSaving(false);
  };

  const copyWebhookSecret = () => {
    navigator.clipboard.writeText(webhookSecret);
    toast.success("Secreto de webhook copiado");
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <OkGoLogo />
        <div>
          <h2 className="text-white text-lg font-bold flex items-center gap-2">
            Integración OkGo
            {configured && (
              <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">Conectado</span>
            )}
          </h2>
          <p className="text-gray-500 text-sm">Conecta OkBot al 100% con OkGo (app.okgo.mx) — auto-registro de usuarios.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-4 bg-white/3 border border-white/8 rounded-xl">
          <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><Users size={14} /> Usuarios sincronizados</div>
          <p className="text-2xl font-bold text-white">{stats.total_okgo_users}</p>
        </div>
        <div className="p-4 bg-white/3 border border-white/8 rounded-xl">
          <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><Key size={14} /> Con PIN genérico</div>
          <p className="text-2xl font-bold text-yellow-400">{stats.still_using_generic_pin}</p>
        </div>
      </div>

      {/* Config */}
      <div className="p-5 bg-red-500/5 border border-red-500/20 rounded-xl space-y-4">
        <div className="flex items-center gap-2">
          <Key size={16} className="text-red-400" />
          <h5 className="text-white font-medium text-sm">Credenciales de la Bubble Data API de OkGo</h5>
        </div>
        <p className="text-gray-500 text-xs">
          OkGo está construido en Bubble.io. Obtén la API Key en <strong className="text-gray-300">Settings → API → Bubble API Token</strong> de la app OkGo.
          {keyPreview && <span className="ml-2 text-green-400">Actual: {keyPreview}</span>}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-gray-500 text-xs mb-1.5 block flex items-center gap-1"><Globe size={12} /> Base URL de OkGo</label>
            <input
              value={cfg.base_url}
              onChange={e => setCfg({ ...cfg, base_url: e.target.value })}
              placeholder="https://app.okgo.mx/api/1.1"
              className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-red-400"
            />
          </div>
          <div>
            <label className="text-gray-500 text-xs mb-1.5 block">API Token de OkGo *</label>
            <input
              type="password"
              value={cfg.api_key}
              onChange={e => setCfg({ ...cfg, api_key: e.target.value })}
              placeholder={configured ? "•••••• (oculto, ingresa nueva para cambiar)" : "Bearer token de Bubble"}
              className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-red-400"
            />
          </div>
          <div>
            <label className="text-gray-500 text-xs mb-1.5 block">PIN genérico para auto-registro</label>
            <input
              value={cfg.default_pin}
              onChange={e => setCfg({ ...cfg, default_pin: e.target.value.replace(/\D/g, "").slice(0, 8) })}
              placeholder="0000"
              maxLength={8}
              className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-red-400 font-mono tracking-widest"
            />
            <p className="text-gray-600 text-[11px] mt-1">4-8 dígitos. Cada usuario OkGo se registrará con este PIN.</p>
          </div>
          <div>
            <label className="text-gray-500 text-xs mb-1.5 block">Auto-registro vía webhook</label>
            <button
              onClick={() => setCfg({ ...cfg, auto_register_enabled: !cfg.auto_register_enabled })}
              className={`w-full py-2.5 rounded-xl text-sm font-medium transition-colors ${
                cfg.auto_register_enabled
                  ? "bg-green-500/20 text-green-400 border border-green-500/40"
                  : "bg-gray-500/20 text-gray-400 border border-gray-500/40"
              }`}
            >
              {cfg.auto_register_enabled ? "✓ Activo" : "Desactivado"}
            </button>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={save}
            disabled={saving}
            className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-medium rounded-xl flex items-center justify-center gap-2"
          >
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
            {saving ? "Guardando..." : "Guardar configuración"}
          </button>
          <button
            onClick={test}
            disabled={testing || !configured}
            className="px-4 py-2.5 bg-white/10 hover:bg-white/15 disabled:opacity-50 text-white text-sm font-medium rounded-xl flex items-center gap-2"
          >
            {testing ? <RefreshCw size={14} className="animate-spin" /> : <Globe size={14} />}
            Probar conexión
          </button>
        </div>

        {status && (
          <div className={`p-3 rounded-xl text-sm border ${
            status.status === "connected"
              ? "bg-green-500/10 border-green-500/30 text-green-400"
              : "bg-red-500/10 border-red-500/30 text-red-400"
          }`}>
            <div className="flex items-center gap-2 font-medium">
              {status.status === "connected" ? <Check size={14} /> : <X size={14} />}
              {status.message}
            </div>
            {status.status === "connected" && status.count !== undefined && (
              <p className="text-xs mt-1 opacity-80">Usuarios disponibles en OkGo: ~{status.count + status.remaining}</p>
            )}
          </div>
        )}
      </div>

      {/* Sync masivo */}
      <div className="p-5 bg-blue-500/5 border border-blue-500/20 rounded-xl space-y-3">
        <div className="flex items-center gap-2">
          <Database size={16} className="text-blue-400" />
          <h5 className="text-white font-medium text-sm">Sincronización masiva (pull)</h5>
        </div>
        <p className="text-gray-500 text-xs">
          Importa todos los usuarios actuales de OkGo y los registra en OkBot con su nickname + el PIN genérico configurado.
        </p>
        <button
          onClick={sync}
          disabled={syncing || !configured}
          className="w-full py-2.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white text-sm font-medium rounded-xl flex items-center justify-center gap-2"
        >
          {syncing ? <RefreshCw size={14} className="animate-spin" /> : <Users size={14} />}
          {syncing ? "Sincronizando…" : "Importar todos los usuarios de OkGo"}
        </button>

        {syncResult && (
          <div className={`p-4 rounded-xl text-sm ${syncResult.success ? "bg-green-500/10 border border-green-500/20" : "bg-red-500/10 border border-red-500/20"}`}>
            <div className="flex items-center gap-2 font-medium mb-2">
              {syncResult.success ? <Check size={14} className="text-green-400" /> : <X size={14} className="text-red-400" />}
              <span className={syncResult.success ? "text-green-400" : "text-red-400"}>{syncResult.message}</span>
            </div>
            {syncResult.success && (
              <div className="grid grid-cols-3 gap-3 mt-2">
                {[
                  { label: "Creados", val: syncResult.created, color: "text-green-400" },
                  { label: "Actualizados", val: syncResult.updated, color: "text-blue-400" },
                  { label: "Con error", val: syncResult.failed, color: "text-red-400" },
                ].map(s => (
                  <div key={s.label} className="bg-white/5 rounded-lg p-2 text-center">
                    <p className={`text-lg font-bold ${s.color}`}>{s.val}</p>
                    <p className="text-gray-500 text-xs">{s.label}</p>
                  </div>
                ))}
              </div>
            )}
            {syncResult.errors?.length > 0 && (
              <div className="mt-2 text-xs text-red-400/70 space-y-0.5">
                {syncResult.errors.map((e, i) => <p key={i}>• {e}</p>)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Webhook */}
      <div className="p-5 bg-purple-500/5 border border-purple-500/20 rounded-xl space-y-3">
        <div className="flex items-center gap-2">
          <Webhook size={16} className="text-purple-400" />
          <h5 className="text-white font-medium text-sm">Auto-registro en tiempo real (push)</h5>
        </div>
        <p className="text-gray-500 text-xs">
          Configura un Backend Workflow en OkGo (Bubble) que se dispare al crear un usuario y llame este endpoint:
        </p>
        <div className="flex gap-2 items-center bg-black/40 border border-white/10 rounded-xl px-3 py-2">
          <code className="flex-1 text-purple-300 text-xs font-mono break-all">{webhookUrl}</code>
          <button onClick={copyWebhook} className="p-1.5 hover:bg-white/10 rounded text-gray-400 hover:text-white">
            <Copy size={14} />
          </button>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/25 p-3 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-white">Secreto de seguridad</p>
              <p className="text-xs text-gray-500">
                {webhookConfigured
                  ? "Configurado. Genera uno nuevo para rotarlo."
                  : "Requerido antes de activar el webhook en Bubble."}
              </p>
            </div>
            <button
              onClick={generateWebhookSecret}
              disabled={saving || !configured}
              className="shrink-0 px-3 py-2 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 disabled:opacity-50 text-purple-200 text-xs font-medium"
            >
              {webhookConfigured ? "Rotar secreto" : "Generar secreto"}
            </button>
          </div>
          {webhookSecret ? (
            <div className="flex gap-2 items-center bg-black/40 border border-purple-500/30 rounded-lg px-3 py-2">
              <code className="flex-1 text-purple-200 text-xs font-mono break-all">{webhookSecret}</code>
              <button onClick={copyWebhookSecret} className="p-1.5 hover:bg-white/10 rounded text-gray-300 hover:text-white" title="Copiar secreto">
                <Copy size={14} />
              </button>
            </div>
          ) : (
            <p className="text-[11px] text-yellow-300/80">Por seguridad, el valor sólo se muestra inmediatamente después de generarlo.</p>
          )}
          <div className="flex gap-2">
            <input
              type="password"
              value={cfg.webhook_secret}
              onChange={e => setCfg({ ...cfg, webhook_secret: e.target.value })}
              placeholder="O establece un secreto propio"
              className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-600 focus:outline-none focus:border-purple-400"
            />
            <button
              onClick={save}
              disabled={saving || (!configured && !cfg.api_key.trim()) || !cfg.webhook_secret.trim()}
              className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 disabled:opacity-50 text-white text-xs font-medium"
            >
              Guardar secreto
            </button>
          </div>
        </div>
        <details className="text-xs text-gray-500">
          <summary className="cursor-pointer hover:text-gray-300">Configuración requerida en Bubble API Connector</summary>
          <p className="mt-2">Añade el header <code className="text-purple-200">X-OkGo-Webhook-Secret</code> con el secreto generado arriba, además del body JSON:</p>
          <pre className="mt-2 bg-black/40 border border-white/10 rounded-lg p-3 text-purple-200 overflow-x-auto">
{`{
  "nickname": "<User's nickname>",
  "email": "<User's email>",
  "name": "<User's name>",
  "first_name": "<User's first_name>",
  "last_name": "<User's last_name>",
  "whatsapp": "<User's whatsapp>",
  "okgo_id": "<User's unique id>"
}`}
          </pre>
          <p className="mt-2 text-gray-600">El usuario se creará en OkBot con su nickname y el PIN genérico ({cfg.default_pin}). Podrá cambiarlo en su primer login.</p>
        </details>
      </div>
    </div>
  );
}
